import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "./AuthContext";

/**
 * Wrap protected routes. Redirects to /login while preserving the intended
 * destination so the user lands back where they tried to go after sign-in.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();

  if (state.status === "loading") {
    return <div style={{ padding: 32 }}>Loading…</div>;
  }
  if (state.status === "anonymous") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

export function RequirePermission({
  permission,
  anyOf,
  children,
}: {
  permission?: string;
  anyOf?: string[];
  children: ReactNode;
}) {
  const { state, hasPermission } = useAuth();
  if (state.status === "loading") return null;

  const hasRequiredPermission = anyOf
    ? anyOf.some((p) => hasPermission(p))
    : permission
      ? hasPermission(permission)
      : false;

  if (state.status === "anonymous" || !hasRequiredPermission) {
    return <Navigate to="/forbidden" replace />;
  }
  return <>{children}</>;
}

/** Gate a route by role union (e.g. CUST_MANAGER | CUST_PROCUREMENT | SUPP_MANAGER). */
export function RequireAnyRole({
  roles,
  children,
}: {
  roles: string[];
  children: ReactNode;
}) {
  const { state, hasRole } = useAuth();
  if (state.status === "loading") return null;
  if (state.status === "anonymous") {
    return <Navigate to="/login" replace />;
  }
  if (!roles.some((r) => hasRole(r))) {
    return <Navigate to="/forbidden" replace />;
  }
  return <>{children}</>;
}
